/**
 * Die Seite „Eigene Wörter“: Liste nach Art, Suche + Hinzufügen in einem Feld,
 * Vorschläge aus den Aufnahmen, das Blatt eines Worts. (23.09.2026; hierher 15.10.2026)
 *
 * Aus SettingsView gezogen: sie braucht von der Klasse nur die Stimme fürs Anhören
 * (`WoerterKontext`). Die Gestalt steht in style.css („Eigene Wörter“).
 */
import { zeichen } from "../zeichen.js";
import { ART_GRUPPE, ART_NAME, VOKABEL_ARTEN, type VokabelArt } from "../../../shared/vokabel-art.js";

/** Ein eigenes Wort, wie es /api/vocabulary liefert. */
export interface WortEintrag {
  id: string;
  term: string;
  pronunciation: string | null;
  art?: VokabelArt | null;
  createdAt: number | string;
}

/** Was die Seite von außen braucht: die Vorlese-Stimme und wer sie abspielt. */
export interface WoerterKontext {
  vorschauStimme(): string;
  spieleVorschau(stimme: string, knopf: HTMLButtonElement, text: string): Promise<void>;
}

/**
 * Sagt Vespera das Wort anders, als es geschrieben ist? Zwei der 57 Wörter
 * trugen als „Aussprache" genau ihre Schreibweise — das ist keine.
 */
function sprichtAnders(e: WortEintrag): boolean {
  const p = (e.pronunciation ?? "").trim();
  return p !== "" && p.toLowerCase() !== e.term.trim().toLowerCase();
}

/**
 * „5. Juli" — mit Jahr nur, wenn es nicht das laufende ist.
 * ⚠️ Ein Eintrag trägt seine Zeit als Text („2026-07-24 09:54:35", naives
 *    UTC aus einer frühen Migration), alle anderen als Millisekunden.
 */
export function seitDatum(wann: number | string, jetzt = new Date()): string {
  const ms =
    typeof wann === "number" ? wann : Date.parse(String(wann).trim().replace(" ", "T") + "Z");
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const d = new Date(ms);
  const jahr = (x: Date): string =>
    x.toLocaleDateString("de-AT", { year: "numeric", timeZone: "Europe/Vienna" });
  return d.toLocaleDateString("de-AT", {
    day: "numeric",
    month: "long",
    ...(jahr(d) === jahr(jetzt) ? {} : { year: "numeric" }),
    timeZone: "Europe/Vienna",
  });
}

/** Die Seite: Satz, Eingabe, Meldung, Vorschlag, Stand, Gruppen, Suchlauf. */
export function baueWoerterSeite(k: WoerterKontext): HTMLElement[] {
  const satz = document.createElement("p");
  satz.className = "einst-seite-satz";
  satz.textContent =
    "Namen und Begriffe, die Vespera beim Zuhören richtig schreiben und " +
    "beim Vorlesen richtig aussprechen soll.";

  // Ein Feld für beides: tippen sucht, Enter oder + fügt hinzu.
  const eingabe = document.createElement("div");
  eingabe.className = "woerter-eingabe";
  const suche = document.createElement("label");
  suche.className = "einst-suche einst-suche--wort";
  const lupe = document.createElement("span");
  lupe.className = "einst-suche-lupe";
  lupe.innerHTML = zeichen("suche", 18);
  const feld = document.createElement("input");
  feld.type = "text";
  feld.className = "einst-suche-feld";
  feld.placeholder = "Wort suchen oder hinzufügen";
  feld.setAttribute("aria-label", "Wort suchen oder hinzufügen");
  feld.setAttribute("enterkeyhint", "done");
  feld.autocomplete = "off";
  feld.maxLength = 80;
  suche.append(lupe, feld);
  const plus = document.createElement("button");
  plus.type = "button";
  plus.className = "woerter-plus";
  plus.setAttribute("aria-label", "Wort hinzufügen");
  plus.innerHTML = zeichen("plus", 20);
  eingabe.append(suche, plus);

  const vorschlag = document.createElement("section");
  vorschlag.className = "woerter-vorschlag";
  vorschlag.hidden = true;

  const meldung = document.createElement("p");
  meldung.className = "woerter-meldung";
  meldung.setAttribute("role", "status");
  meldung.hidden = true;
  const melde = (text: string): void => {
    meldung.textContent = text;
    meldung.hidden = !text;
  };

  const stand = document.createElement("p");
  stand.className = "woerter-stand";
  const gruppen = document.createElement("div");
  gruppen.className = "woerter-gruppen";

  const suchlauf = document.createElement("button");
  suchlauf.type = "button";
  suchlauf.className = "woerter-suchlauf";
  suchlauf.textContent = "In deinen Aufnahmen nach neuen Wörtern suchen";

  let eintraege: WortEintrag[] = [];
  // Welche Gruppen ganz aufgeklappt sind — bleibt beim Neuzeichnen.
  const aufgeklappt = new Set<string>();
  /** So viele Marken je Gruppe, dann „+ N weitere". */
  const GRENZE = 10;

  const zeichne = (): void => {
    const n = eintraege.length;
    const mitAussprache = eintraege.filter((e) => sprichtAnders(e)).length;
    stand.textContent =
      `${n} ${n === 1 ? "Wort" : "Wörter"} · ${mitAussprache} mit Aussprache` +
      " · nach Art sortiert";
    const q = feld.value.trim().toLowerCase();
    gruppen.textContent = "";
    let treffer = 0;
    for (const art of [...VOKABEL_ARTEN, null]) {
      const liste = eintraege.filter(
        (e) =>
          (e.art ?? null) === art &&
          (q === "" ||
            e.term.toLowerCase().includes(q) ||
            (e.pronunciation ?? "").toLowerCase().includes(q)),
      );
      if (liste.length === 0) continue;
      treffer += liste.length;
      const schluessel = art ?? "ohne";
      const abschnitt = document.createElement("section");
      abschnitt.className = "woerter-gruppe";
      abschnitt.dataset.art = schluessel;
      const h = document.createElement("h3");
      h.className = "einst-gruppe-titel";
      h.textContent = art ? ART_GRUPPE[art] : "Noch nicht eingeordnet";
      const marken = document.createElement("div");
      marken.className = "woerter-marken";
      // Ein einzelnes „+ 1 weitere" spart nichts — dann gleich alle.
      const alle = q !== "" || aufgeklappt.has(schluessel) || liste.length <= GRENZE + 1;
      for (const e of alle ? liste : liste.slice(0, GRENZE)) {
        const m = document.createElement("button");
        m.type = "button";
        m.className = "wort-marke";
        m.dataset.id = e.id;
        m.textContent = e.term;
        if (sprichtAnders(e)) {
          m.classList.add("wort-marke--spricht");
          const s = document.createElement("span");
          s.className = "wort-marke-spricht";
          s.textContent = `spricht: ${e.pronunciation ?? ""}`;
          m.appendChild(s);
        }
        m.addEventListener("click", () => oeffneWortBlatt(k, e, laden));
        marken.appendChild(m);
      }
      if (!alle) {
        const mehr = document.createElement("button");
        mehr.type = "button";
        mehr.className = "woerter-mehr";
        mehr.textContent = `+ ${liste.length - GRENZE} weitere`;
        mehr.addEventListener("click", () => {
          aufgeklappt.add(schluessel);
          zeichne();
        });
        marken.appendChild(mehr);
      }
      abschnitt.append(h, marken);
      gruppen.appendChild(abschnitt);
    }
    if (q && treffer === 0) {
      const leer = document.createElement("p");
      leer.className = "woerter-leer";
      leer.textContent = `„${feld.value.trim()}“ steht noch nicht in der Liste — mit + hinzufügen.`;
      gruppen.appendChild(leer);
    } else if (n === 0) {
      const leer = document.createElement("p");
      leer.className = "woerter-leer";
      leer.textContent = "Noch keine eigenen Wörter.";
      gruppen.appendChild(leer);
    }
  };

  const laden = async (): Promise<void> => {
    try {
      const r = await fetch("/api/vocabulary", { credentials: "same-origin" });
      const d = (await r.json()) as { entries?: WortEintrag[] };
      eintraege = d.entries ?? [];
      zeichne();
    } catch {
      melde("Die Wörter lassen sich gerade nicht laden.");
    }
  };

  const hinzufuegen = async (): Promise<void> => {
    const wort = feld.value.trim();
    if (!wort) {
      feld.focus();
      return;
    }
    // Steht es schon da, öffnet + das Wort, statt es doppelt anzulegen.
    const da = eintraege.find((e) => e.term.toLowerCase() === wort.toLowerCase());
    if (da) {
      oeffneWortBlatt(k, da, laden);
      return;
    }
    plus.disabled = true;
    try {
      const r = await fetch("/api/vocabulary", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ term: wort }),
      });
      const d = (await r.json()) as { ok?: boolean; entry?: WortEintrag | null };
      if (!r.ok || !d.ok) throw new Error(String(r.status));
      feld.value = "";
      await laden();
      const art = d.entry?.art;
      melde(
        art
          ? `„${wort}“ steht jetzt bei „${ART_GRUPPE[art]}“.`
          : `„${wort}“ ist drin — tipp es an, um es einzuordnen.`,
      );
    } catch {
      melde("Das Wort ließ sich gerade nicht speichern.");
    } finally {
      plus.disabled = false;
    }
  };
  feld.addEventListener("input", () => {
    melde("");
    zeichne();
  });
  feld.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      void hinzufuegen();
    }
  });
  plus.addEventListener("click", () => void hinzufuegen());

  interface Vorschlag {
    id: string;
    wort: string;
    grund: string;
    belege: number;
  }
  const zeigeVorschlag = (liste: Vorschlag[]): void => {
    vorschlag.textContent = "";
    const v = liste[0];
    vorschlag.hidden = !v;
    if (!v) return;
    const h = document.createElement("h3");
    h.className = "einst-gruppe-titel";
    h.textContent = "Vorschlag aus deinen Aufnahmen";
    const zeile = document.createElement("div");
    zeile.className = "woerter-vorschlag-zeile";
    const text = document.createElement("span");
    text.className = "woerter-vorschlag-text";
    // Ein Feld, kein Etikett: die Schreibweise lässt sich vorher ändern.
    const wort = document.createElement("input");
    wort.type = "text";
    wort.className = "woerter-vorschlag-wort";
    wort.value = v.wort;
    wort.maxLength = 80;
    wort.setAttribute("aria-label", `Schreibweise für ${v.wort}`);
    const grund = document.createElement("span");
    grund.className = "woerter-vorschlag-grund";
    grund.textContent =
      (v.grund || "Vorschlag") +
      (v.belege ? ` · ${v.belege}-mal in deinen Aufnahmen` : "");
    text.append(wort, grund);
    const ja = document.createElement("button");
    ja.type = "button";
    ja.className = "woerter-ja";
    ja.textContent = "Übernehmen";
    const nein = document.createElement("button");
    nein.type = "button";
    nein.className = "woerter-nein";
    nein.textContent = "Nein";
    nein.title = "Nicht mehr vorschlagen";
    const entscheide = (aktion: "annehmen" | "ablehnen"): void => {
      ja.disabled = true;
      nein.disabled = true;
      void fetch("/api/vocabulary/vorschlaege/" + encodeURIComponent(v.id), {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aktion, wort: wort.value.trim() }),
      })
        .then(async () => {
          if (aktion === "annehmen") await laden();
          await holeVorschlaege();
        })
        .catch(() => {
          ja.disabled = false;
          nein.disabled = false;
        });
    };
    ja.addEventListener("click", () => entscheide("annehmen"));
    nein.addEventListener("click", () => entscheide("ablehnen"));
    wort.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        entscheide("annehmen");
      }
    });
    zeile.append(text, ja, nein);
    vorschlag.append(h, zeile);
    if (liste.length > 1) {
      const mehr = document.createElement("p");
      mehr.className = "woerter-vorschlag-mehr";
      mehr.textContent = `Noch ${liste.length - 1} weitere · `;
      const alle = document.createElement("button");
      alle.type = "button";
      alle.className = "woerter-alle";
      // Die Zahl steht auf dem Knopf: man weiß, worauf man drückt.
      alle.textContent = `Alle übernehmen (${liste.length})`;
      alle.addEventListener("click", () => {
        alle.disabled = true;
        void fetch("/api/vocabulary/vorschlaege/alle", {
          method: "POST",
          credentials: "same-origin",
        })
          .then((r) => r.json())
          .then(async (d: { ok?: boolean; uebernommen?: number; gesamt?: number }) => {
            const n = d.uebernommen ?? 0;
            /*
             * ⚠️ Die Zahl nennen, nicht „erledigt". Wurde weniger
             *    übernommen als offen war, gehört das gesagt.
             */
            melde(
              !d.ok
                ? "Das hat gerade nicht geklappt — bitte nochmal."
                : n === (d.gesamt ?? n)
                  ? `${n} ${n === 1 ? "Wort" : "Wörter"} übernommen.`
                  : `${n} von ${d.gesamt} übernommen — der Rest ist offen geblieben.`,
            );
            await laden();
            await holeVorschlaege();
          })
          .catch(() => {
            melde("Das hat gerade nicht geklappt — bitte nochmal.");
            alle.disabled = false;
          });
      });
      mehr.appendChild(alle);
      vorschlag.appendChild(mehr);
    }
  };
  const holeVorschlaege = async (): Promise<void> => {
    try {
      const r = await fetch("/api/vocabulary/vorschlaege", {
        credentials: "same-origin",
      });
      const d = (await r.json()) as { vorschlaege?: Vorschlag[] };
      zeigeVorschlag(d.vorschlaege ?? []);
    } catch {
      zeigeVorschlag([]);
    }
  };

  suchlauf.addEventListener("click", () => {
    suchlauf.disabled = true;
    suchlauf.textContent = "Sucht …";
    melde("Ich sehe deine letzten Aufzeichnungen und Projekte durch …");
    void fetch("/api/vocabulary/vorschlaege/suchen", {
      method: "POST",
      credentials: "same-origin",
    })
      .then((r) => r.json())
      .then((d: { ok?: boolean; neu?: number; error?: string }) => {
        if (d.ok) {
          melde(
            d.neu
              ? `${d.neu} neue ${d.neu === 1 ? "Wort" : "Wörter"} gefunden.`
              : "Nichts Neues gefunden — die Liste ist auf dem Stand.",
          );
          void holeVorschlaege();
        } else {
          melde(d.error ?? "Der Suchlauf hat gerade nicht geklappt.");
        }
      })
      .catch(() => melde("Der Suchlauf hat gerade nicht geklappt."))
      .finally(() => {
        suchlauf.disabled = false;
        suchlauf.textContent = "In deinen Aufnahmen nach neuen Wörtern suchen";
      });
  });

  void laden();
  void holeVorschlaege();
  return [satz, eingabe, meldung, vorschlag, stand, gruppen, suchlauf];
}

/**
 * Ein Wort bearbeiten — das Blatt von unten. (23.09.2026)
 *
 * Schreibweise, Aussprache (nur wenn Vespera es falsch sagt), Art. Entfernen
 * steht nur hier und will zweimal gedrückt werden: auf der Liste stand es
 * 57-mal in Rot, einen Fingerbreit neben dem Wort.
 *
 * Gespeichert wird mit „Fertig"; Schließen verwirft.
 */
function oeffneWortBlatt(k: WoerterKontext, e: WortEintrag, nachher: () => Promise<void>): void {
  const d = document.createElement("dialog");
  d.className = "wort-blatt";
  d.setAttribute("aria-labelledby", "wort-blatt-titel");

  const griff = document.createElement("span");
  griff.className = "wort-blatt-griff";
  griff.setAttribute("aria-hidden", "true");

  const kopf = document.createElement("div");
  kopf.className = "wort-blatt-kopf";
  const titel = document.createElement("h2");
  titel.id = "wort-blatt-titel";
  titel.className = "wort-blatt-titel";
  titel.textContent = e.term;
  const zu = document.createElement("button");
  zu.type = "button";
  zu.className = "wort-blatt-zu";
  zu.setAttribute("aria-label", "Schließen");
  zu.innerHTML = zeichen("schliessen", 20);
  // Der Fokus landet auf dem Schließen-Knopf, nicht im Feld — sonst
  // springt am iPhone sofort die Tastatur hoch.
  zu.autofocus = true;
  kopf.append(titel, zu);

  const feld = (id: string, beschriftung: string, leise?: string): {
    huelle: HTMLElement;
    input: HTMLInputElement;
  } => {
    const huelle = document.createElement("div");
    huelle.className = "wort-blatt-feld";
    const l = document.createElement("label");
    l.htmlFor = id;
    l.className = "wort-blatt-beschriftung";
    l.textContent = beschriftung;
    if (leise) {
      const s = document.createElement("span");
      s.className = "wort-blatt-leise";
      s.textContent = ` ${leise}`;
      l.appendChild(s);
    }
    const input = document.createElement("input");
    input.type = "text";
    input.id = id;
    input.className = "wort-blatt-eingabe";
    input.maxLength = 80;
    input.autocomplete = "off";
    huelle.appendChild(l);
    return { huelle, input };
  };
  const schrift = feld("wort-blatt-schrift", "So wird es geschrieben");
  schrift.input.value = e.term;
  schrift.huelle.appendChild(schrift.input);

  const sprich = feld(
    "wort-blatt-sprich",
    "So wird es gesprochen",
    "(nur wenn Vespera es falsch sagt)",
  );
  sprich.input.value = e.pronunciation ?? "";
  sprich.input.placeholder = "z. B. Hollen-ton";
  const reihe = document.createElement("div");
  reihe.className = "wort-blatt-reihe";
  const hoeren = document.createElement("button");
  hoeren.type = "button";
  hoeren.className = "wort-blatt-hoeren";
  hoeren.setAttribute("aria-label", "Aussprache anhören");
  hoeren.innerHTML = zeichen("abspielen", 16);
  hoeren.addEventListener("click", () => {
    const text = sprich.input.value.trim() || schrift.input.value.trim();
    if (!text) return;
    // Mit der Stimme, die auch vorliest — sonst hört man eine andere.
    void k.spieleVorschau(k.vorschauStimme(), hoeren, text);
  });
  reihe.append(sprich.input, hoeren);
  sprich.huelle.appendChild(reihe);

  const arten = document.createElement("fieldset");
  arten.className = "wort-blatt-arten";
  const legende = document.createElement("legend");
  legende.className = "wort-blatt-beschriftung";
  legende.textContent = "Art";
  const knoepfe = document.createElement("div");
  knoepfe.className = "wort-blatt-art-reihe";
  let art: VokabelArt | null = e.art ?? null;
  const artKnoepfe: HTMLButtonElement[] = [];
  for (const a of VOKABEL_ARTEN) {
    const k = document.createElement("button");
    k.type = "button";
    k.className = "wort-art";
    k.dataset.art = a;
    k.textContent = ART_NAME[a];
    k.setAttribute("aria-pressed", String(a === art));
    k.addEventListener("click", () => {
      // Ein zweiter Tipp auf dieselbe Art nimmt die Einordnung zurück.
      art = art === a ? null : a;
      for (const x of artKnoepfe) x.setAttribute("aria-pressed", String(x.dataset.art === art));
    });
    artKnoepfe.push(k);
    knoepfe.appendChild(k);
  }
  arten.append(legende, knoepfe);

  const seit = document.createElement("p");
  seit.className = "wort-blatt-seit";
  const datum = seitDatum(e.createdAt);
  seit.textContent = datum ? `Seit ${datum} in der Liste` : "";
  seit.hidden = !datum;

  const meldung = document.createElement("p");
  meldung.className = "wort-blatt-meldung";
  meldung.setAttribute("role", "alert");
  meldung.hidden = true;
  const melde = (t: string): void => {
    meldung.textContent = t;
    meldung.hidden = !t;
  };

  const fuss = document.createElement("div");
  fuss.className = "wort-blatt-fuss";
  const entfernen = document.createElement("button");
  entfernen.type = "button";
  entfernen.className = "wort-blatt-entfernen";
  entfernen.textContent = "Entfernen";
  const fertig = document.createElement("button");
  fertig.type = "button";
  fertig.className = "wort-blatt-fertig";
  fertig.textContent = "Fertig";
  fuss.append(entfernen, fertig);

  d.append(griff, kopf, schrift.huelle, sprich.huelle, arten, seit, meldung, fuss);

  const schliessen = (): void => {
    if (d.open) d.close();
  };
  d.addEventListener("close", () => d.remove());
  zu.addEventListener("click", schliessen);
  // Ein Tipp neben das Blatt schließt es — wie überall bei Blättern.
  d.addEventListener("click", (ev) => {
    if (ev.target === d) schliessen();
  });

  entfernen.addEventListener("click", () => {
    if (entfernen.dataset.sicher !== "ja") {
      entfernen.dataset.sicher = "ja";
      entfernen.textContent = "Wirklich entfernen?";
      return;
    }
    entfernen.disabled = true;
    void fetch("/api/vocabulary/" + encodeURIComponent(e.id), {
      method: "DELETE",
      credentials: "same-origin",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status));
        schliessen();
        await nachher();
      })
      .catch(() => {
        entfernen.disabled = false;
        melde("Das Wort ließ sich gerade nicht entfernen.");
      });
  });

  fertig.addEventListener("click", () => {
    const patch: { term?: string; pronunciation?: string | null; art?: VokabelArt | null } = {};
    const term = schrift.input.value.trim();
    const pron = sprich.input.value.trim();
    if (term !== e.term) patch.term = term;
    if (pron !== (e.pronunciation ?? "")) patch.pronunciation = pron || null;
    if (art !== (e.art ?? null)) patch.art = art;
    if (Object.keys(patch).length === 0) {
      schliessen();
      return;
    }
    if (patch.term === "") {
      melde("Ein Wort braucht eine Schreibweise.");
      return;
    }
    fertig.disabled = true;
    void fetch("/api/vocabulary/" + encodeURIComponent(e.id), {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    })
      .then(async (r) => {
        if (r.status === 409) {
          melde(`„${term}“ steht schon in der Liste.`);
          return;
        }
        if (!r.ok) throw new Error(String(r.status));
        schliessen();
        await nachher();
      })
      .catch(() => melde("Das ließ sich gerade nicht speichern."))
      .finally(() => {
        fertig.disabled = false;
      });
  });

  document.body.appendChild(d);
  d.showModal();
}
